import { useState, useContext, useEffect, useReducer } from 'react';
import { ActionIcon, Alert, Center, Group, Text, Loader, Table, TextInput, Switch, ScrollArea, LoadingOverlay } from '@mantine/core';
import { IconSearch, IconAlertCircle, IconRefresh } from '@tabler/icons';
import { TitleContainer } from '../../shared/TitleContainer';
import AppContext from '../../shared/AppContext';
import { useTranslation } from 'react-i18next';

interface RoleInterface {
    role_id: number,
    role_label: string,
    is_granted: boolean,
}
interface PermissionInterface {
    api_id: number,
    api_code: string,
    api_label: string,
    roles: RoleInterface[],
};
interface FamilyInterface {
    family: string,
    data: PermissionInterface[],
}

const permissionsReducer = (state: any, action: any) => {
    switch(action.type) {
        case 'LOAD':
            return {
                ...state,
                isLoading: false,
                isUpdating: false,
                loaded: action.data,
                filtered: JSON.parse(JSON.stringify(action.data)),
            }
        case 'START_UPDATE':
            return {
                ...state,
                isUpdating: true,
            }
        case 'UPDATE':
            const ai = action.data.api_id;
            const ri = action.data.role_id;
            const ll = JSON.parse(JSON.stringify(state.loaded));
            for(let f=0; f<ll.length; f++) {
                for(let a=0; a<ll[f].data.length; a++) {
                    for(let r=0; r<ll[f].data[a].roles.length;r++){
                        if (ai === ll[f].data[a].api_id && ri === ll[f].data[a].roles[r].role_id) {
                            ll[f].data[a].roles[r].is_granted = action.data.value;
                        }
                    }
                }
            }
            const ff = JSON.parse(JSON.stringify(state.filtered));
            for(let f=0; f<ff.length; f++) {
                for(let a=0; a<ff[f].data.length; a++) {
                    for(let r=0; r<ff[f].data[a].roles.length;r++){
                        if (ai === ff[f].data[a].api_id && ri === ff[f].data[a].roles[r].role_id) {
                            ff[f].data[a].roles[r].is_granted = action.data.value;
                        }
                    }
                }
            }
            return {
                ...state,
                isUpdating: false,
                loaded: ll,
                filtered: ff,
            }
        case 'FILTER':
            const w = action.term;
            const ss = JSON.parse(JSON.stringify(state.loaded));
            if (w.length === 0) {
                return {
                    ...state,
                    filtered: ss,
                }
            }
            for(let f=0; f<ss.length; f++) {
                for(let a=ss[f].data.length-1; a>=0; a--) {
                    if (ss[f].data[a].api_code.search(w) === -1 && ss[f].data[a].api_label.search(w) === -1) {
                        ss[f].data.splice(a, 1);
                    }
                }
            }
            return {
                ...state,
                filtered: ss,
            }
        case 'ERROR':
            return {
                ...state,
                isLoading: false,
                error: action.message || 'The API says no.',
            }
        case 'RESET':
            return {
                isLoading: true,
                isUpdating: false,
                error: null,
                loaded: [],
                filtered: [],
            }
        default:
            return {
                ...state,
                isLoading: false,
                error: 'Oops, unknown local error.',
            }
    };
};


const permissionLabels: Record<string, string> = {
  // Family
  'Account': 'Account', 'Annuaire': 'Address Book', 'Banque': 'Banking',
  'Besoin': 'Need', 'Chasse': 'Hunt', 'Configuration': 'Settings',
  'CV-theque': 'CV Library', 'Divers': 'Misc', 'Mission': 'Mission',
  // Labels
  'Faire une demande RH': 'Submit HR request',
  'Afficher les messages': 'View messages', 'Effectuer un mailing': 'Send a mailing',
  'Exporter les contacts': 'Export contacts', 'Liste des contacts': 'Contact list',
  'Liste des societes': 'Company list', 'Liste types de messages': 'Message type list',
  'Lister natures de messages': 'List message types', 'Modifier un contact': 'Edit a contact',
  'Modifier un message': 'Edit a message', 'Modifier une societe': 'Edit a company',
  'Rechercher dans les contacts': 'Search contacts', 'Tableau de bord': 'Dashboard',
  'Afficher un lettrage': 'View a lettering', 'Inserer un releve CSV': 'Insert CSV statement',
  'Lister les releves bancaires': 'List bank statements', 'Realiser un lettrage': 'Create a lettering',
  'Supprimer un lettrage': 'Remove a lettering',
  'Acceder a un besoin': 'Access a need', 'Affecter un besoin': 'Assign a need',
  'Attacher un CV a un besoin': 'Attach CV to a need', 'Cloturer un besoin': 'Close a need',
  'Creer un besoin': 'Create a need', 'Creer un contrat': 'Create a contract',
  'Historique besoin': 'Need history', 'Inserer ou supprimer un document': 'Insert or delete a document',
  'Jeux de donnees besoins': 'Need datasets', 'Lister les besoins': 'List needs',
  'Lister les documents': 'List documents', 'MAJ processus besoin': 'Update need process',
  'Mettre a jour un besoin': 'Update a need', 'Supprimer un besoin': 'Delete a need',
  'Visualiser le processus besoin': 'View need process', 'Visualiser un document': 'View a document',
  'Affecter une chasse': 'Assign a hunt', 'Analyser un projet LinkedIn': 'Analyze LinkedIn project',
  'Attacher un profil a une chasse': 'Attach profile to a hunt',
  'Generer un CV word LinkedIn': 'Generate LinkedIn Word CV',
  'Gerer les recherches': 'Manage searches', 'Lister les projets LinkedIn': 'List LinkedIn projects',
  'MAJ processus chasse': 'Update hunt process', 'MAJ projet LinkedIn': 'Update LinkedIn project',
  'Recuperer un projet LinkedIn': 'Retrieve LinkedIn project',
  'Sauvegarder donnees LinkedIn': 'Save LinkedIn data',
  'Visualiser processus chasse': 'View hunt process',
  'Acceder a sa liste des taches': 'Access task list',
  'Afficher la matrice API roles': 'View API roles matrix',
  'Afficher la matrice utilisateurs roles': 'View users roles matrix',
  'Afficher les donnees de compte': 'View account data', 'Afficher les roles': 'View roles',
  'Afficher les utilisateurs': 'View users', 'Ajouter un utilisateur': 'Add a user',
  'Envoyer un email': 'Send an email', 'Exporter un jeu de donnees': 'Export a dataset',
  'Mettre a jour un compte': 'Update an account', 'Modifier son mot de passe': 'Change password',
  'Modifier une affectation': 'Edit an assignment', 'Modifier une permission': 'Edit a permission',
  'Re initialiser son mot de passe': 'Reset password', 'Se connecter': 'Sign in',
  'Se deconnecter': 'Sign out', 'Visualiser organigramme': 'View org chart',
  'Affecter un CV': 'Assign a CV', 'Analyser les meta donnees CV': 'Analyze CV metadata',
  'Bloquer ou debloquer un CV': 'Block or unblock a CV', 'CV non valides': 'Unvalidated CVs',
  'Historique CV': 'CV history', 'Inserer un CV': 'Insert a CV',
  'Jeux de donnees CV': 'CV datasets', 'Liste des metiers': 'Job list',
  'Liste des mots': 'Word list', 'Liste des secteurs': 'Sector list',
  'Lister les CV bloques': 'List blocked CVs', 'Lister les doublons': 'List duplicates',
  'Lister les pieces attachees': 'List attachments', 'MAJ piece attachee': 'Update attachment',
  'MAJ test MBTI': 'Update MBTI test', 'Modifier un CV valide': 'Edit a validated CV',
  'Obtenir le test MBTI': 'Get MBTI test', 'Qualifier le CV': 'Qualify CV',
  'Re affecter une action': 'Reassign an action', 'Re initialiser qualification': 'Reset qualification',
  'Rechercher des CV': 'Search CVs', 'Recuperer un CV': 'Retrieve a CV',
  'Recuperer un document CV': 'Retrieve CV document', 'Recuperer une piece attachee': 'Retrieve attachment',
  'Soumettre CV pour controle': 'Submit CV for review', 'Soumettre CV pour validation': 'Submit CV for validation',
  'Supprimer le CV': 'Delete CV', 'Tableau de bord CV': 'CV dashboard',
  'Traiter un doublon': 'Process a duplicate', 'Valider un CV': 'Validate a CV',
  'Rechercher une ville': 'Search a city',
  'Afficher un document contractuel': 'View contractual document',
  'Afficher un rapport': 'View a report', 'Afficher une facture client': 'View client invoice',
  'Afficher une facture fournisseur': 'View supplier invoice',
  'Afficher une mission': 'View a mission', 'Associer un message': 'Associate a message',
  'Auditer les missions': 'Audit missions', 'Cloturer une mission': 'Close a mission',
  'Creer un rapport': 'Create a report', 'Creer une facture': 'Create an invoice',
  'Creer une mission': 'Create a mission', 'Historique mission': 'Mission history',
  'Lire les messages': 'Read messages', 'Lister les documents contractuels': 'List contractual documents',
  'Lister les factures clients': 'List client invoices',
  'Lister les factures fournisseurs': 'List supplier invoices',
  'Lister les missions': 'List missions', 'Lister les rapports': 'List reports',
  'MAJ documents contractuels': 'Update contractual documents',
  'MAJ en-tete mission': 'Update mission header', 'MAJ factures clients': 'Update client invoices',
  'MAJ factures fournisseurs': 'Update supplier invoices', 'MAJ rapports': 'Update reports',
  'Mettre a jour une meta donnee': 'Update metadata', 'Rechercher dans une mission': 'Search in a mission',
  'Restaurer une mission': 'Restore a mission', 'Supprimer une mission': 'Delete a mission',
  'Tableau des flux financiers': 'Financial flows table',
  'Trajectoire financiere': 'Financial trajectory',
  'Trajectoire financiere globale': 'Global financial trajectory',
  'Visualiser la TVA': 'View VAT',
};

function translatePerm(text: string): string {
  var lang = localStorage.getItem('language') || 'fr';
  if (lang === 'en') {
    return permissionLabels[text] || text;
  }
  return text;
}

const ManagePermissions = () => {

    const myContext = useContext(AppContext);
  const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const apiUrlGet: string = myContext.apiAddress + '/get_repository_permission';
    const apiUrlUpdate: string = myContext.apiAddress + '/update_permission';
    
    const [ permissions, dispatchPermissions ] = useReducer(
        permissionsReducer, {
            isLoading: true,
            isUpdating: false,
            error: null,
            loaded: [],
            filtered: [],
        }
    );

    const handleChange = (api_id: number, role_id: number, value: boolean) => {
        const myForm = new FormData();
        myForm.append('api_id', api_id + '');
        myForm.append('role_id', role_id + '');
        myForm.append('value', value ? '1' : '0');
        dispatchPermissions({type: 'START_UPDATE'});
        myContext.httpClient.post(apiUrlUpdate, myForm).then((result: any) => {
            if (result.data.status === true) {
                dispatchPermissions({type: 'UPDATE', data: { api_id: api_id, role_id: role_id, value: value} });
            } else if (result.data.status === false) {
                dispatchPermissions({type: 'ERROR', message: result.data.message});
            } else {
                dispatchPermissions({type: 'ERROR', message: 'Strange, the servor returned a bad answer.'});
            }
        });
    }

    const handleSearch = (v: string) => {
        setSearch(v);
        dispatchPermissions({type: 'FILTER', term: v.trim().toLocaleLowerCase()});
    }

    useEffect(() => {
        if (!permissions.isLoading) return;
        if (apiUrlGet.startsWith('null')) return;
        myContext.httpClient.get(apiUrlGet).then((result: any) => {
            if (result.data.status === true) {
                dispatchPermissions({type: 'LOAD', data: result.data.data});
            } else if (result.data.status === false) {
                dispatchPermissions({type: 'ERROR', message: result.data.message});
            } else {
                dispatchPermissions({type: 'ERROR', message: 'Strange, the servor returned a bad answer.'});
            }
        });
    }, [permissions.isLoading]); // eslint-disable-line

    if (permissions.isLoading) {
        return (
            <Center>
                <Group>
                    <Loader color="blue" size="md" />
                    <Text color="blue">{t("configuration.loadingPermissions")}</Text>
                </Group>
            </Center>
        );
    }

    if (permissions.error !== null) {
        return (
            <Center>
                <Alert icon={<IconAlertCircle size={16} />} title={t('configuration.gestionDesPermissions')} color="red" radius="md">
                    {permissions.error}
                </Alert>
            </Center>
        )
    }

    if (permissions.filtered.length === 0) {
        return (
            <Center>
                <Alert icon={<IconAlertCircle size={16} />} title={t('configuration.gestionDesPermissions')} color="red" radius="md">
                    Aucune permission à afficher.
                </Alert>
            </Center>
        )
    }

    return (
        <>
        <Group position='apart'>
            <TitleContainer>{t('configuration.gestionDesPermissions')}</TitleContainer>
            <ActionIcon color='blue' variant='outline' onClick={() => dispatchPermissions({type: 'RESET'})}>
                <IconRefresh size={16} />
            </ActionIcon>
        </Group>
        <TextInput
            placeholder={t("configuration.searchApi")}
            mb="md"
            icon={<IconSearch size={14} stroke={1.5} />}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
        />
        <ScrollArea style={{height: 'calc(100vh - 160px)'}}>
            <LoadingOverlay visible={permissions.isUpdating} />
            {permissions.filtered.map((f: FamilyInterface) => (
            <Table sx={{ minWidth: 1000 }} key={'p_family_' + f.family} highlightOnHover>
                <tbody>        
                    {(f.data.length === 0) ? (<></>) : (
                        <>
                        <tr>
                            <td colSpan={1 + f.data[0].roles.length}><strong>{translatePerm(f.family)}</strong></td>
                        </tr>
                        {f.data.map((p: PermissionInterface) => (
                            <tr key={'p_api_' + p.api_id}>
                                <td style={{width: '250px'}}>
                                    <Text size='xs' color="dimmed">{p.api_code}</Text>
                                    <Text>{translatePerm(p.api_label)}</Text>
                                </td>
                                {p.roles.map((r) => (
                                    <td key={'p_' + p.api_id + '_' + r.role_id}>
                                        <Group spacing='xs'>
                                            <Switch size="xs" color="blue" checked={r.is_granted} onChange={(event) => handleChange(p.api_id, r.role_id, event.currentTarget.checked)} />
                                            <Text size="xs">{r.role_label}</Text>
                                        </Group>
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </>
                    )}
                </tbody>
            </Table>
            ))}
        </ScrollArea>
        </>
    )
}

export { ManagePermissions }